import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useController } from 'react-hook-form';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { useCurrentBlock } from '../CurrentBlockProvider';
import { SettingsComponent } from '../../lib/declarations/EditorComponentTypes';
import { BlockSpecificPage } from '../../lib/declarations/BlockSpecificPage';
import ActionMenu from '../../../components/ActionMenu';
import { MediaViewType } from '../../../declarations/models/blocks/MediaViewType';
import CustomIcon from '../../../components/CustomIcon';
import Container from '../../../components/Container';
import { AttachmentsBlock } from '../../../declarations/models/blocks/AttachmentsBlock';
import { AttachmentLayout } from '../../../declarations/models/AttachmentLayout';
import { BaseBlock } from '../../../declarations/models/blocks/BaseBlock';
import SkinSelector from '../../lib/components/settings/SkinSelector';
import { Skin } from '../../../declarations/models/Skin';
import { useEditorData } from '../../lib/components/EditorDataProvider';

const LAYOUT_OPTIONS: Array<AttachmentLayout> = Object.values(AttachmentLayout);
const LAYOUT_OPTIONS_ICONS: Record<AttachmentLayout, ReactNode> = {
  [AttachmentLayout.EXTENDED]: <CustomIcon name='banner_image_above' />,
  [AttachmentLayout.SIMPLE]: <MaterialSymbol name='list' />,
  [AttachmentLayout.NORMAL]: <CustomIcon name='banner_image_left' />,
};

enum ViewTypeWidth {
  NORMAL = 'normal',
  WIDE = 'wide',
}

enum ViewTypeVariant {
  LIST = 'list',
}

// Avalable widths for different layouts:
const AVAILABLE_LAYOUT_WIDTHS: Record<AttachmentLayout, Array<null | ViewTypeWidth>> = {
  [AttachmentLayout.SIMPLE]: [ViewTypeWidth.NORMAL, ViewTypeWidth.WIDE],
  [AttachmentLayout.NORMAL]: [ViewTypeWidth.NORMAL, ViewTypeWidth.WIDE],
  [AttachmentLayout.EXTENDED]: [ViewTypeWidth.NORMAL],
};

const allMediaViewTypes = Object.values(MediaViewType).map(String);
if (Object.values(ViewTypeVariant).some((v) => !allMediaViewTypes.includes(v))) {
  throw new Error('The enum MediaViewTypeVariant MUST be a subset of the enum MediaViewType');
}

const VARIANT_ICONS: Record<ViewTypeVariant, ReactNode> = {
  [ViewTypeVariant.LIST]: <MaterialSymbol name='list' />,
};

const WIDTH_ICONS: Record<ViewTypeWidth | 'null', ReactNode> = {
  null: <CustomIcon name='width_normal' />,
  [ViewTypeWidth.NORMAL]: <CustomIcon name='width_normal' />,
  [ViewTypeWidth.WIDE]: <CustomIcon name='width_wider' />,
};

const AVAILABLE_WIDTHS: Record<ViewTypeVariant, Array<null | ViewTypeWidth>> = {
  [ViewTypeVariant.LIST]: [null, ViewTypeWidth.WIDE],
};

export const PageBlockAttachmentsViewSetting: SettingsComponent = () => {
  const { t } = useTranslation('components');
  const { blockPath } = useCurrentBlock();
  const { skins } = useEditorData();

  const availableSkins = useMemo<Array<Skin>>(() => {
    return skins?.filter((skin) => skin.scope?.includes('grid') !== false);
  }, [skins]);

  const {
    field: { value: skinClass, onChange: setSkinClass },
  } = useController<BlockSpecificPage<AttachmentsBlock>, `${typeof blockPath}.skin`>({ name: `${blockPath}.skin` });

  const handleSkinChanged = (skin: Skin | null) => {
    setSkinClass(skin?.class || '');
  };

  const selectedSkin = useMemo<Skin | null>(() => {
    return availableSkins.find((skin) => skin.class === skinClass) || null;
  }, [availableSkins, skinClass]);

  const {
    field: { value, onChange },
  } = useController<BlockSpecificPage<AttachmentsBlock>, `${typeof blockPath}.view`>({
    name: `${blockPath}.view`,
  });

  const {
    field: { value: selectedwidth, onChange: onChangeWidth },
  } = useController<BlockSpecificPage<AttachmentsBlock>, `${typeof blockPath}.width`>({
    name: `${blockPath}.width`,
  });

  const currentWidth = useMemo<null | ViewTypeWidth>(() => {
    if (value?.toString()?.endsWith(ViewTypeWidth.WIDE)) {
      return ViewTypeWidth.WIDE;
    }
    return null;
  }, [value]);

  const currentVariant = useMemo<ViewTypeVariant>(() => {
    return Object.values(ViewTypeWidth).reduce((previousValue, width) => {
      return previousValue?.replace(`-${width}`, '');
    }, value?.toString()) as unknown as ViewTypeVariant;
  }, [value]);

  const handleChange = (newVariant: ViewTypeVariant, newWidth: ViewTypeWidth | null) => {
    if (newWidth !== null && AVAILABLE_WIDTHS[newVariant].includes(newWidth)) {
      onChange(`${newVariant}-${newWidth}` as MediaViewType);
    } else {
      onChange(newVariant as string as MediaViewType);
    }
  };

  const {
    field: { value: layout, onChange: onChangeLayout },
  } = useController<BlockSpecificPage<AttachmentsBlock>, `${typeof blockPath}.layout`>({
    name: `${blockPath}.layout`,
  });

  const selectableLayoutWidths = AVAILABLE_LAYOUT_WIDTHS[layout || AttachmentLayout.NORMAL] || [];

  return (
    <Container gap={0}>
      {selectableLayoutWidths.length > 1 && (
        <ActionMenu
          id='select-attachment-block-view-type-width'
          ariaLabel={t('ViewTypeSelector.SelectViewTypeWidth')}
          tooltip={t('ViewTypeSelector.SelectViewTypeWidth')}
          tooltipPlacement='top'
          icon={WIDTH_ICONS[(selectedwidth as ViewTypeWidth) || 'null']}>
          {selectableLayoutWidths.map((width) => (
            <MenuItem
              key={width || 'none-option'}
              selected={width === selectedwidth}
              disabled={width === selectedwidth}
              onClick={() => onChangeWidth(width)}>
              <ListItemIcon>{WIDTH_ICONS[width || 'null']}</ListItemIcon>
              <ListItemText>{t(`PageBlockAttachmentViewSetting.ViewTypeWidth.${width || 'none'}`)}</ListItemText>
            </MenuItem>
          ))}
        </ActionMenu>
      )}

      <ActionMenu
        id='page-block-layout-selector'
        ariaLabel={t('components.PageBlockModuleLayoutSetting.ActionMenuLabel')}
        tooltip={t('PageBlockModuleLayoutSetting.Tooltip')}
        tooltipPlacement='top'
        icon={layout ? LAYOUT_OPTIONS_ICONS[layout as AttachmentLayout] : LAYOUT_OPTIONS_ICONS.normal}>
        {LAYOUT_OPTIONS.map((layoutOption) => (
          <MenuItem
            key={layoutOption}
            onClick={() => onChangeLayout(layoutOption)}
            selected={layout === layoutOption}
            aria-label={t(`AudioBlock.layouts.${layoutOption}`)}>
            <ListItemIcon>{LAYOUT_OPTIONS_ICONS[layoutOption]}</ListItemIcon>
            <ListItemText>{t(`AudioBlock.layouts.${layoutOption}`)}</ListItemText>
          </MenuItem>
        ))}
      </ActionMenu>
      {layout !== AttachmentLayout.SIMPLE && (
        <SkinSelector availableSkins={availableSkins} selectedSkin={selectedSkin} onSkinChange={handleSkinChanged} />
      )}
    </Container>
  );
};

export default PageBlockAttachmentsViewSetting;
